import { useEffect, useId, type ReactNode } from "react"
import { Link, useLocation } from "react-router-dom"
import {
  ClipboardCheck,
  FileText,
  History,
  LayoutDashboard,
  ListTree,
  PanelLeftClose,
  PanelLeftOpen,
  PenLine,
  ScanEye,
  Send,
  Users,
  type LucideIcon,
} from "lucide-react"
import { cn } from "@/lib/utils"
import { Separator } from "@/components/ui/separator"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { useIsMobile } from "@/hooks/use-mobile"
import { bidLifecycleOf } from "@/lib/bid-lifecycle"
import { useBidsStore } from "@/stores/bids-store"
import { useDocumentsStore } from "@/stores/documents-store"
import { useBidMembersStore } from "@/stores/bid-members-store"
import { useBidMenuStore } from "@/stores/bid-menu-store"
import { useQualifyAttentionCount } from "@/components/bid-menu/useQualifyAttentionCount"
import {
  BID_SECTION_LABELS,
  BID_WORK_AREAS,
  bidSectionPath,
  getBidSection,
  isBidEditorPath,
  type BidSection,
  type BidWorkArea,
} from "@/lib/bid-sections"

const SECTION_ICONS: Record<BidSection, LucideIcon> = {
  overview: LayoutDashboard,
  qualify: ClipboardCheck,
  plan: ListTree,
  write: PenLine,
  review: ScanEye,
  submit: Send,
  files: FileText,
  team: Users,
  activity: History,
}

export interface BidMenuProps {
  bidId: string
  /**
   * The bid's current stage. Marks that work area with a small dot. When left
   * out, the menu reads it from the loaded bid (R4 stage model); only an
   * active bid has one.
   */
  currentStage?: BidWorkArea | null
}

// Matches the app rail's vertical-nav treatment: neutral wash + weight for
// active, never colour.
const ITEM_BASE =
  "flex h-8 items-center gap-2 rounded-[6px] text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring [&>svg]:shrink-0"
// Expanded rows pair a 16px icon with the 14px label. Collapsed, the icon
// carries the whole meaning, so it takes the app rail's 20px size in a 40px
// chip. The chip keeps the row's h-8, so toggling animates width only.
const ITEM_EXPANDED = "w-full px-3 [&>svg]:h-4 [&>svg]:w-4"
const ITEM_COLLAPSED = "mx-auto w-10 justify-center px-0 [&>svg]:h-5 [&>svg]:w-5"
const ITEM_IDLE = "text-sidebar-foreground hover:bg-foreground/[0.05] hover:text-foreground"
const ITEM_ACTIVE = "bg-foreground/[0.07] font-medium text-foreground"

function MenuTooltip({ label, show, children }: { label: string; show: boolean; children: ReactNode }) {
  if (!show) return <>{children}</>
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="right">{label}</TooltipContent>
    </Tooltip>
  )
}

function MenuItem({
  bidId,
  section,
  isActive,
  collapsed,
  count,
  countLabel,
  attention,
  isCurrentStage,
}: {
  bidId: string
  section: BidSection
  isActive: boolean
  collapsed: boolean
  count?: number | undefined
  countLabel?: string | undefined
  attention?: number | undefined
  isCurrentStage?: boolean | undefined
}) {
  const Icon = SECTION_ICONS[section]
  const label = BID_SECTION_LABELS[section]
  const hasAttention = attention !== undefined && attention > 0
  return (
    <li className="relative">
      <MenuTooltip label={label} show={collapsed}>
        <Link
          to={bidSectionPath(bidId, section)}
          aria-current={isActive ? "page" : undefined}
          aria-label={collapsed ? label : undefined}
          data-testid={`bid-menu-${section}`}
          className={cn(
            ITEM_BASE,
            isActive ? ITEM_ACTIVE : ITEM_IDLE,
            collapsed ? ITEM_COLLAPSED : ITEM_EXPANDED
          )}
        >
          <Icon aria-hidden="true" />
          {!collapsed && <span className="min-w-0 flex-1 truncate">{label}</span>}
          {!collapsed && isCurrentStage && (
            <span
              data-testid="bid-menu-stage-marker"
              aria-label="Current stage"
              className="h-1.5 w-1.5 shrink-0 rounded-full bg-foreground"
            />
          )}
          {!collapsed && hasAttention && (
            <span
              aria-label={`${attention} ${attention === 1 ? "item" : "items"} needing attention in ${label}`}
              className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[hsl(var(--ai-accent))] px-1.5 text-[10px] font-medium leading-none text-[hsl(var(--ai-accent-foreground))]"
            >
              {attention}
            </span>
          )}
          {!collapsed && !hasAttention && count !== undefined && count > 0 && (
            <span className="text-meta tabular-nums" aria-label={countLabel}>
              {count}
            </span>
          )}
        </Link>
      </MenuTooltip>
      {collapsed && hasAttention && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute right-1.5 top-1 h-1.5 w-1.5 rounded-full bg-[hsl(var(--ai-accent))]"
        />
      )}
    </li>
  )
}

/**
 * The bid workspace menu (R4): a left column beside the app rail on every
 * `/app/bids/:bidId/...` page. Replaces the nine horizontal bid tabs.
 */
export function BidMenu({ bidId, currentStage: currentStageProp }: BidMenuProps) {
  const bidContextId = useId()
  const location = useLocation()
  const isMobile = useIsMobile()
  const onEditor = isBidEditorPath(location.pathname)
  const active = getBidSection(location.pathname)

  const storedCollapsed = useBidMenuStore((s) => (onEditor ? s.editorCollapsed : s.collapsed))
  const setCollapsed = useBidMenuStore((s) => s.setCollapsed)
  // Phones always get the icon strip: there is no room for a second column.
  const collapsed = isMobile || storedCollapsed

  const bid = useBidsStore((s) => (s.selectedBid?.id === bidId ? s.selectedBid : null))
  const fetchBidById = useBidsStore((s) => s.fetchBidById)
  const currentStage: BidWorkArea | null =
    currentStageProp !== undefined ? currentStageProp : bid ? bidLifecycleOf(bid).stage : null
  const documentsBidId = useDocumentsStore((s) => s.currentBidId)
  const documentCount = useDocumentsStore((s) => (s.currentBidId === bidId ? s.documents.length : 0))
  const fetchDocuments = useDocumentsStore((s) => s.fetchDocuments)
  const memberCount = useBidMembersStore((s) => (s.bidId === bidId ? s.members.length : 0))
  const ensureMembers = useBidMembersStore((s) => s.ensure)
  const qualifyAttention = useQualifyAttentionCount(bidId)

  // Bid pages load the bid and its documents themselves (BidDetailsPage). An
  // editor opened straight from a link does not, so the menu fills the gap.
  const hasBid = bid !== null
  useEffect(() => {
    if (onEditor && !hasBid) void fetchBidById(bidId)
  }, [onEditor, hasBid, bidId, fetchBidById])

  useEffect(() => {
    if (onEditor && documentsBidId !== bidId) void fetchDocuments(bidId)
  }, [onEditor, documentsBidId, bidId, fetchDocuments])

  useEffect(() => {
    void ensureMembers(bidId)
  }, [bidId, ensureMembers])

  const ToggleIcon = collapsed ? PanelLeftOpen : PanelLeftClose
  const toggleLabel = collapsed ? "Expand bid menu" : "Collapse bid menu"

  return (
    <TooltipProvider delayDuration={0}>
      <nav
        aria-label="Bid"
        aria-describedby={bid ? bidContextId : undefined}
        data-collapsed={collapsed ? "true" : "false"}
        className={cn(
          "flex h-full shrink-0 flex-col border-r border-border bg-background transition-[width] duration-200 motion-reduce:transition-none",
          collapsed ? "w-14" : "w-52"
        )}
      >
        {bid && <span id={bidContextId} className="sr-only">{bid.title}</span>}
        <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-2 pt-3">
          <ul className="flex flex-col gap-1">
            <MenuItem bidId={bidId} section="overview" isActive={active === "overview"} collapsed={collapsed} />
          </ul>

          {collapsed ? (
            <Separator className="mx-auto my-2 w-10" />
          ) : (
            <p className="px-3 pb-1 pt-4 text-meta">Work areas</p>
          )}

          <ul className="flex flex-col gap-1">
            {BID_WORK_AREAS.map((area) => (
              <MenuItem
                key={area}
                bidId={bidId}
                section={area}
                isActive={active === area}
                collapsed={collapsed}
                attention={area === "qualify" ? qualifyAttention : undefined}
                isCurrentStage={currentStage === area}
              />
            ))}
          </ul>

          <Separator className={cn("my-2", collapsed ? "mx-auto w-10" : "mx-1 w-auto")} />

          <ul className="flex flex-col gap-1">
            <MenuItem
              bidId={bidId}
              section="files"
              isActive={active === "files"}
              collapsed={collapsed}
              count={documentCount}
              countLabel={`${documentCount} tender ${documentCount === 1 ? "file" : "files"}`}
            />
            <MenuItem
              bidId={bidId}
              section="team"
              isActive={active === "team"}
              collapsed={collapsed}
              count={memberCount}
              countLabel={`${memberCount} team ${memberCount === 1 ? "member" : "members"}`}
            />
            <MenuItem bidId={bidId} section="activity" isActive={active === "activity"} collapsed={collapsed} />
          </ul>
        </div>

        {!isMobile && (
          <div className="px-2 pb-3 pt-1">
            <MenuTooltip label={toggleLabel} show={collapsed}>
              <button
                type="button"
                onClick={() => setCollapsed(!collapsed, onEditor)}
                aria-label={toggleLabel}
                aria-expanded={!collapsed}
                className={cn(
                  ITEM_BASE,
                  ITEM_IDLE,
                  "text-muted-foreground",
                  collapsed ? ITEM_COLLAPSED : ITEM_EXPANDED
                )}
              >
                <ToggleIcon aria-hidden="true" />
                {!collapsed && <span className="text-meta">Collapse</span>}
              </button>
            </MenuTooltip>
          </div>
        )}
      </nav>
    </TooltipProvider>
  )
}
